import type { FC, PropsWithChildren } from 'react';
import { useCallback, useContext } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import ConfigContext from 'app/common/ConfigContext';
import { formatDate } from 'app/utils/date';
import {
  getEBattleSubdivisionLabels,
  hasDetailedStats,
  isEbattle
} from 'app/config/videogameSpecific.config.helper';
import ArrowIcon from 'app/icons/arrow-min-right.svg';
import TeamAvatar from 'app/components/TeamAvatar';
import VideogameVersionTag from 'app/components/VideogameVersionTag';
import { getSubdivisionScore } from 'app/components/Blocks/HeadToHeadBlock/utils';

import style from './style.module.scss';

interface Props {
  encounter: Encounter;
  onSelect: (encounter: Encounter) => void;
  opponent1Id: number | undefined;
  opponent2Id: number | undefined;
  selectable?: boolean;
}

const HeadToHeadEncounter: FC<Props> = ({
  encounter,
  onSelect,
  opponent1Id,
  opponent2Id,
  selectable = true
}) => {
  const { lang } = useContext(ConfigContext);
  const { t } = useTranslation();

  const opponent1 = encounter.opponents.find(opponent => opponent.id === opponent1Id);
  const opponent2 = encounter.opponents.find(opponent => opponent.id === opponent2Id);

  const isFixtureOnly =
    !encounter.detailed_stats ||
    !hasDetailedStats(encounter.videogame_slug) ||
    !encounter.games.some(game => game.complete);

  const tabIndexProps = !selectable ? { tabIndex: -1 } : {};

  const isETennis = encounter.videogame_slug === 'e-tennis';
  const isEBattleSub = !isETennis && isEbattle(encounter.videogame_slug);
  const slug = encounter.videogame_slug as EBattleSlug;
  const ebattleLabels = isEBattleSub ? getEBattleSubdivisionLabels(slug, lang) : null;

  const WrapperElement: FC<PropsWithChildren> = useCallback(
    ({ children }) =>
      isFixtureOnly ? (
        <div
          className={classNames(style.headToHeadEncounter, {
            [style.fixtureOnlyMatch]: isFixtureOnly,
            [style.ebattleEncounter]: isEBattleSub,
            [style.etennisEncounter]: isETennis
          })}
        >
          {children}
        </div>
      ) : (
        <button
          className={classNames(style.headToHeadEncounter, {
            [style.etennisEncounter]: isETennis
          })}
          onClick={() => onSelect(encounter)}
          {...tabIndexProps}
          type="button"
        >
          {children}
        </button>
      ),
    [selectable]
  );

  if (!opponent1 || !opponent2) {
    return null;
  }

  if (isETennis) {
    const tennisLabels = getEBattleSubdivisionLabels('e-tennis', lang);
    const maxSets = tennisLabels.length;
    const gridStyle = { '--set-count': maxSets } as React.CSSProperties;
    const opp1Sets = opponent1.sets ?? [];
    const opp2Sets = opponent2.sets ?? [];
    const opp1Pad = maxSets - opp1Sets.length;
    const opp2Pad = maxSets - opp2Sets.length;

    const opp1SetsWon = opp1Sets.filter(s => {
      const otherSet = opp2Sets.find(os => os.index === s.index);
      return otherSet !== undefined && s.score > otherSet.score;
    }).length;
    const opp2SetsWon = opp2Sets.filter(s => {
      const otherSet = opp1Sets.find(os => os.index === s.index);
      return otherSet !== undefined && s.score > otherSet.score;
    }).length;

    return (
      <WrapperElement>
        <div className={style.etennisGrid} style={gridStyle}>
          <span className={style.encounterDate}>
            {formatDate(new Date(encounter.begin_at), lang, { year: undefined })}
          </span>
          <span
            className={classNames(style.etennisName, {
              [style.etennisSetWinnerName]: opp1SetsWon > opp2SetsWon
            })}
          >
            {opponent1.name}
          </span>
          {opp1Sets.map(s => {
            const otherSet = opp2Sets.find(os => os.index === s.index);
            const isSetWinner = otherSet !== undefined && s.score > otherSet.score;
            return (
              <span
                key={s.index}
                className={classNames(style.etennisSetScore, {
                  [style.etennisSetWinner]: isSetWinner
                })}
              >
                {s.score}
              </span>
            );
          })}
          {Array.from({ length: opp1Pad }, (_, i) => (
            <span key={`pad-${i}`} />
          ))}

          <span />
          <span
            className={classNames(style.etennisName, {
              [style.etennisSetWinnerName]: opp2SetsWon > opp1SetsWon
            })}
          >
            {opponent2.name}
          </span>
          {opp2Sets.map(s => {
            const otherSet = opp1Sets.find(os => os.index === s.index);
            const isSetWinner = otherSet !== undefined && s.score > otherSet.score;
            return (
              <span
                key={s.index}
                className={classNames(style.etennisSetScore, {
                  [style.etennisSetWinner]: isSetWinner
                })}
              >
                {s.score}
              </span>
            );
          })}
          {Array.from({ length: opp2Pad }, (_, i) => (
            <span key={`pad-${i}`} />
          ))}
        </div>
      </WrapperElement>
    );
  }

  if (isEBattleSub && ebattleLabels) {
    return (
      <WrapperElement>
        <span className={style.encounterDate}>
          {formatDate(new Date(encounter.begin_at), lang, { year: undefined })}
        </span>
        {ebattleLabels.map((_, i) => {
          const subIndex = i + 1;
          const h1 = getSubdivisionScore(opponent1, slug, subIndex);
          const h2 = getSubdivisionScore(opponent2, slug, subIndex);
          return (
            <span
              key={subIndex}
              className={classNames(style.ebattleSubCell, {
                [style.ebattleSubCellWithSep]: i > 0
              })}
            >
              <span className={classNames({ [style.halfDimmed]: h1 < h2 })}>{h1}</span>
              <span className={style.halfDimmed}> - </span>
              <span className={classNames({ [style.halfDimmed]: h2 < h1 })}>{h2}</span>
            </span>
          );
        })}
        <span className={style.ebattleTotal}>
          <span
            className={classNames({
              [style.winnerScore]: opponent1.score > opponent2.score,
              [style.loserScore]: opponent1.score < opponent2.score
            })}
          >
            {opponent1.score}
          </span>
          <span className={style.ebattleTotalSep}>-</span>
          <span
            className={classNames({
              [style.winnerScore]: opponent2.score > opponent1.score,
              [style.loserScore]: opponent2.score < opponent1.score
            })}
          >
            {opponent2.score}
          </span>
        </span>
      </WrapperElement>
    );
  }

  return (
    <WrapperElement>
      <span className={style.encounterTitle}>
        <span className={style.encounterLeague}>{encounter.league_name}</span>
        <span className={style.encounterDate}>
          {formatDate(new Date(encounter.begin_at), lang, { year: '2-digit' })}
        </span>
        <VideogameVersionTag name={encounter.videogame_title} />
      </span>
      <span className={style.encounterScore}>
        <TeamAvatar team={opponent1} className={style.teamLogo} />
        <span className={classNames({ [style.lowestWinrate]: opponent1.score < opponent2.score })}>
          {opponent1.score}
        </span>
        <span> - </span>
        <span className={classNames({ [style.lowestWinrate]: opponent2.score < opponent1.score })}>
          {opponent2.score}
        </span>
        <TeamAvatar team={opponent2} className={style.teamLogo} />
      </span>
      <span className={style.encounterSeeMore}>
        <span className={style.seeMoreText}>{t('all-see_more')}</span>
        <ArrowIcon className={style.arrow} />
      </span>
    </WrapperElement>
  );
};

HeadToHeadEncounter.displayName = 'HeadToHeadEncounter';

export default HeadToHeadEncounter;
